/**
 * Formatos del WhatsApp oficial. Puros: sin React, sin fetch, sin localStorage.
 *
 * El saldo, los cobros y los costos del oficial son en DÓLARES (Twilio cobra en USD), y sólo en dólares desde el
 * 26/10/2026. `ars()` queda para las ventas del comercio, que vienen en pesos de su base (la recompra medida).
 */

import type { Cupo24h } from "../types";
import { money, num, pct, plural } from "../../campana-nueva/utils/format";

/**
 * "USD 12,40" (es-AR). null/undefined/NaN → "—": un dato que no está no se disfraza de 0.
 * Lo que redondea a cero con esos decimales (el -0 o el resto de coma flotante que deja el rebase
 * del saldo, -6,25e-13) se muestra «USD 0,00»: Intl lo pintaría «USD -0,00».
 */
export function usd(n: number | null | undefined, decimales = 2): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  const x = Math.abs(Number(n)) < 0.5 * 10 ** -decimales ? 0 : Number(n);
  return "USD " + x.toLocaleString("es-AR", { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}

/**
 * El mes en palabras («octubre»), para los ejemplos de los campos («Te extrañamos octubre»). Estaban
 * fijos en «septiembre» y en octubre ya parecían un error de la pantalla.
 */
export function mesEnPalabras(hoy: Date = new Date()): string {
  return hoy.toLocaleDateString("es-AR", { month: "long" });
}

/**
 * «2026-10» → «octubre de 2026». El período del gasto del mes llegaba crudo a la pantalla («Gastado este
 * mes · 2026-10»). Lo que no tiene esa forma vuelve tal cual: un dato raro no se esconde.
 */
export function mesDelPeriodo(periodo: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})$/.exec(String(periodo ?? "").trim());
  if (!m) return String(periodo ?? "");
  const mes = Number(m[2]);
  if (mes < 1 || mes > 12) return String(periodo);
  return new Date(Number(m[1]), mes - 1, 1).toLocaleDateString("es-AR", { month: "long", year: "numeric" });
}

/** Costo por mensaje con 4 decimales: "USD 0,0802" (con 2 decimales todas las tarifas dirían 0,08). */
/**
 * Lo que hay que CARGAR, al centavo hacia arriba. El saldo que falta tiene los decimales del costo de un
 * mensaje (0,0668 × 1,2 = 0,08016): con `usd` decía «faltan USD 0,08», se cargaba eso y el saldo quedaba
 * 0,00016 abajo. El mismo redondeo que `centavoHaciaArriba` del backend.
 */
export function usdHaciaArriba(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  const x = Number(n) <= 0 ? 0 : Math.ceil(Math.round(Number(n) * 1e6) / 1e4) / 100;
  return usd(x);
}

export function usdPorMensaje(n: number | null | undefined): string {
  return usd(n, 4);
}

/** Los movimientos del saldo que son el cobro (o la devolución) de UN mensaje: van con el precio unitario, a 4 decimales. */
const MOVIMIENTOS_POR_MENSAJE: ReadonlySet<string> = new Set(["consumo", "reintegro", "consumo_entrante", "consumo_sesion"]);

/**
 * El monto de un movimiento del saldo, con signo: «+USD 50,00» o «−USD 0,0060». Una recarga o un ajuste son plata de
 * la marca y van con 2 decimales, como el saldo; el cobro o la devolución de un mensaje es un precio unitario y va con
 * 4 SIEMPRE. Antes se decidía por el monto (menos de un centavo, 4; si no, 2): un envío de USD 0,0123 decía «USD 0,01»
 * al lado de otro de «USD 0,0060», y la misma columna mezclaba precisiones.
 */
export function montoMovimiento(m: { tipo: string; montoUSD: number | null | undefined }): string {
  const n = m?.montoUSD === null || m?.montoUSD === undefined ? NaN : Number(m.montoUSD);
  if (!Number.isFinite(n)) return "—";
  const texto = MOVIMIENTOS_POR_MENSAJE.has(m.tipo) ? usdPorMensaje(Math.abs(n)) : usd(Math.abs(n));
  return `${n >= 0 ? "+" : "−"}${texto}`;
}

/** = money() de campana-nueva ("$ 12.400"); null → "—". */
export function ars(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return money(Math.round(Number(n)));
}

/** Antigüedad en horas → texto: 0.4 → "hace menos de 1 h"; 5 → "hace 5 h"; 50 → "hace 2 días". */
export function horasTexto(h: number | null | undefined): string {
  if (h === null || h === undefined || !Number.isFinite(Number(h)) || Number(h) < 0) return "—";
  const x = Number(h);
  if (x < 1) return "hace menos de 1 h";
  if (x < 24) return `hace ${Math.floor(x)} h`;
  const dias = Math.floor(x / 24);
  return `hace ${dias} ${plural(dias, "día", "días")}`;
}

const dos = (n: number) => String(n).padStart(2, "0");

/** Date → "2026-09-24T10:00" en la hora LOCAL del navegador (lo que espera un input datetime-local). */
function aLocal(d: Date): string {
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}T${dos(d.getHours())}:${dos(d.getMinutes())}`;
}

/** ISO → valor de `<input type="datetime-local">` en hora del navegador. Inválido → "". */
export function isoADatetimeLocal(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : aLocal(d);
}

/**
 * Valor de `datetime-local` → ISO. Convierte con la hora LOCAL del navegador: mandar el string
 * crudo lo corre 3 h (el backend lo leería como UTC), el mismo error que ya documenta
 * `ProgramarEnvio.tsx`. La franja la valida el backend en la zona del negocio. Inválido → null.
 */
export function datetimeLocalAIso(v: string): string | null {
  if (!v || typeof v !== "string") return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/** Rango del selector de fecha de programación: desde dentro de 5 min hasta dentro de 30 días. */
export function limitesProgramacion(ahora: Date): { min: string; max: string } {
  const min = new Date(ahora.getTime() + 5 * 60 * 1000);
  const max = new Date(ahora.getTime() + 30 * 24 * 60 * 60 * 1000);
  return { min: aLocal(min), max: aLocal(max) };
}

/**
 * Tile «Disponibles hoy».
 *  - ilimitado → «Sin límite», sin barra (el backend manda limite y disponibles en null SÓLO ahí).
 *  - si no → disponibles, «de L personas cada 24 h · N usadas» y la barra usados/límite.
 * Nunca «se asume 250»: el «sin dato de Meta» ya llega como 250 desde el backend. Si igual llegara
 * un null sin `ilimitado` (contrato roto), se dice que no hay dato; no se inventa un número.
 */
export function textoCupo(c: Cupo24h): { valor: string; sub: string; meterPct: number | null } {
  const usados = Number(c?.usados) || 0;
  const usadas = `${num(usados)} ${plural(usados, "usada", "usadas")}`;
  if (c?.ilimitado) {
    return { valor: "Sin límite", sub: `Meta no limita a este número · ${usadas} en 24 h`, meterPct: null };
  }
  if (c?.limite === null || c?.limite === undefined || !Number.isFinite(Number(c.limite))) {
    return { valor: "Sin dato", sub: `Meta no informó el límite · ${usadas} en 24 h`, meterPct: null };
  }
  const limite = Number(c.limite);
  const disponibles = c.disponibles === null || c.disponibles === undefined ? Math.max(0, limite - usados) : Number(c.disponibles);
  // Con el freno por calidad, el límite es la mitad del de Meta: se dice, o «1.000» contradice al número de Meta.
  const porCalidad = c.frenoCalidad
    ? ` (la mitad${typeof c.limiteMeta === "number" ? ` de ${num(c.limiteMeta)}` : ""}, por la calidad${c.frenoCalidad === "pausa" ? "; el marketing está en pausa" : ""})`
    : "";
  return {
    valor: num(disponibles),
    sub: `de ${num(limite)} ${plural(limite, "persona", "personas")} cada 24 h${porCalidad} · ${usadas}`,
    meterPct: Math.min(100, pct(usados, limite)),
  };
}

/**
 * Cómo se muestra un destino. Revelado: tal cual. Si no, sólo los últimos 4 dígitos, con el
 * prefijo de Argentina cuando se reconoce («549…» → «+54 9 ••• ••• 1234»). Con menos de 4 dígitos
 * no hay nada seguro que mostrar. Es el ÚNICO enmascarado del front (bitácora, previa, constancia y
 * respuestas): el backend ya manda los números enmascarados, pero si un día llegara uno completo,
 * ninguna pantalla lo muestra entero sin que se haya pedido (D-C).
 */
export function destinoParaMostrar(destino: string | null | undefined, revelado: boolean): string {
  const s = String(destino || "");
  if (revelado) return s || "—";
  const digitos = s.replace(/\D/g, "");
  if (digitos.length < 4) return "•••";
  const ultimos = digitos.slice(-4);
  const inicio = s.replace(/^\+/, "");
  if (inicio.startsWith("549")) return `+54 9 ••• ••• ${ultimos}`;
  if (inicio.startsWith("54")) return `+54 ••• ••• ${ultimos}`;
  return `••• ••• ${ultimos}`;
}

/**
 * El nombre de un análisis de ventas en el selector de la Audiencia y en la tabla de Consentimientos:
 * «Reactivación de octubre de 2026 · HATSU · 245 clientes · 8/10/2026 14:32». Reactivación los llama a todos
 * «Reactivación de <mes>», y el selector mostraba opciones idénticas («Reactivación de octubre de 2026 · 2026-10 ·
 * 8/10/2026»): era fácil mandarle la campaña a la audiencia equivocada. La hora, cuántos clientes trae y el comercio
 * los distinguen; el período se va (ya lo dicen el nombre y la fecha). No renombra nada guardado. Lo que no viene (un
 * backend anterior no manda la marca ni los clientes) no se muestra.
 */
export function etiquetaAnalisis(a: { nombre?: string | null; marca?: string | null; clientes?: number | null; createdAt?: string | null }): string {
  const nombre = String(a?.nombre ?? "").trim() || "Análisis sin nombre";
  const partes = [nombre];
  const marca = String(a?.marca ?? "").trim();
  // «HATSU · HATSU» no distingue nada: si el nombre ya lo dice, no se repite.
  if (marca && !nombre.toLowerCase().includes(marca.toLowerCase())) partes.push(marca);
  const clientes = a?.clientes;
  if (typeof clientes === "number" && Number.isFinite(clientes)) partes.push(`${num(clientes)} ${plural(clientes, "cliente", "clientes")}`);
  const d = a?.createdAt ? new Date(a.createdAt) : null;
  if (d && !Number.isNaN(d.getTime())) {
    const dos = (n: number) => String(n).padStart(2, "0");
    partes.push(`${d.toLocaleDateString("es-AR")} ${dos(d.getHours())}:${dos(d.getMinutes())}`);
  }
  return partes.join(" · ");
}

/**
 * Quién hizo algo en una campaña, dicho para el dueño. El detalle mostraba el email del token tal cual («Confirmada …
 * por alan vía hatsu-demo@operador.deenex»). Misma regla que `autorDe` del backend (`http.util.ts`), que firma lo que
 * se manda desde Conversaciones: el nombre que declaró el operador y, sin él, «Equipo Deenex». Lo que hizo el sistema
 * solo (un corte, una reanudación automática) es «Automático». El mail de alguien del comercio va tal cual: es suyo.
 * Vacío → "" (quien lo muestra decide el «—»).
 */
export function nombreDeQuien(email: string | null | undefined): string {
  const e = String(email ?? "").trim();
  if (!e) return "";
  if (e === "sistema") return "Automático";
  const via = e.indexOf(" vía ");
  if (via > 0) return e.slice(0, via).trim();
  if (e.endsWith("@operador.deenex")) return "Equipo Deenex";
  if (e.endsWith("@dev.local")) return "Desarrollo";
  return e;
}

/**
 * Quién hizo algo, dicho para una persona de un CLIENTE (Accesos, rol «comercio»). El token del equipo lleva el mail de
 * la persona de Deenex («alan@deenex.tech vía hatsu@operador.deenex») y `nombreDeQuien` lo muestra: al equipo le sirve,
 * pero un cliente no tiene por qué ver el mail de quién de Deenex cargó, confirmó o declaró algo. Para él es «Equipo
 * de Deenex» (también si el backend ya lo mandó así). Lo de alguien de su comercio (su mail tal cual), el sistema o
 * desarrollo, como `nombreDeQuien`.
 */
export function nombreDeQuienParaCliente(email: string | null | undefined): string {
  return esAlguienDelEquipo(email) ? EQUIPO_PARA_CLIENTE : nombreDeQuien(email);
}

/** Cómo se nombra a alguien del equipo para una persona de un cliente (el backend ya le manda «equipo de Deenex»). */
export const EQUIPO_PARA_CLIENTE = "Equipo de Deenex";

/**
 * ¿Es alguien del equipo de Deenex? Por el mail del token del equipo («… vía hatsu@operador.deenex») o porque el
 * backend ya lo reemplazó por «equipo de Deenex» (lo hace con todo lo que le manda a un token de cliente).
 */
function esAlguienDelEquipo(x: string | null | undefined): boolean {
  const e = String(x ?? "").trim();
  return e.endsWith("@operador.deenex") || /^equipo de deenex$/i.test(e);
}

/** `nombreDeQuien` para el equipo y `nombreDeQuienParaCliente` para una persona de un cliente. */
export function nombreDeQuienPara(email: string | null | undefined, equipo: boolean): string {
  return equipo ? nombreDeQuien(email) : nombreDeQuienParaCliente(email);
}

/**
 * Quién mandó algo desde Conversaciones (el `autor` que firma el backend con `autorDe`). Para alguien del equipo, el
 * nombre es el mail de la persona de Deenex: a una persona de un cliente se le dice «Equipo de Deenex».
 */
export function nombreDeAutorPara(autor: { nombre?: string; tipo?: string } | null | undefined, equipo: boolean): string {
  if (!equipo && (autor?.tipo === "operador_deenex" || esAlguienDelEquipo(autor?.nombre))) return EQUIPO_PARA_CLIENTE;
  return String(autor?.nombre ?? "");
}
